/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/no-this-alias*/
import BaseModel from './base-model';
import {
    IObservableArray,
    observable,
} from 'mobx';
import StyleHandler from '@src/slide/domain/ui-model/adapter/style-handler';

//  lodash部分引入
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';

import {
    ElementBaseSdkTypeV3, IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type AnimationModel from '@src/slide/domain/ui-model/element-modeles/anim-model';
import type { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import type { ISmartArtModel } from '@src/slide/domain/ui-model/element-modeles/Interface/smart-art-model.interface';
import {
    IFrame,
    IFrameProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import Frame from '@src/slide/domain/ui-model/element-modeles/property/frame';
import { IUpdateAndRemoveProps } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import {
    IElementModelProps,
    IElementModelStyle,
} from '@src/slide/domain/ui-model/element-modeles/Interface/model-props/element-model-props.interface';
import { ILinkProperty } from '@src/slide/domain/ui-model/element-modeles/property/link/link.interface';
import { LinkProperty } from '@src/slide/domain/ui-model/element-modeles/property/link';
import { IFillProperty } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { IEffect } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { Effect } from '@src/slide/domain/ui-model/element-modeles/property/effect';
import Fill from '@src/slide/domain/ui-model/element-modeles/property/fill';
import { SourceType } from '@src/slide/domain/ui-model/constant/element-source';
import { enumerateGroup, enumerateSimilarGroup } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { IBorderProperty } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import Border from '@src/slide/domain/ui-model/element-modeles/property/border';
import { ILockProperty } from '@src/slide/domain/ui-model/element-modeles/property/lock/lock.interface';
import Lock from '@src/slide/domain/ui-model/element-modeles/property/lock';
import UpdateAndRemoveLogic from '@src/slide/domain/ui-model/element-modeles/property/fill/update-remove';
import { IBaseGraphics, createGraphicsParser, GraphicsType } from '@tencent/alloy-graphics';
import type { BaseFill } from '../../plugin/referModels/FillClass';
import { isSketchStyle } from '@src/slide/domain/ui-model/element-modeles/property/border/sketch/sketch-style';
import {
    ElementCommonPropertyConverter,
} from '@src/slide/domain/ui-model/converter/node-property/element/element-common-property-converter';
import {
    STPlaceholderType, IShapeData,
    ICTShapeStyle,
} from '@tencent/slide-sdk-model';
import { IPlaceHolderProps } from '@src/slide/domain/ui-model/element-modeles/property/placeholder/placeholder.interface';
import { I3DProperty, IScene3D, IShape3D } from '@src/slide/domain/ui-model/element-modeles/property/3d/property3d.interface';
import ThreeDProperty from '@src/slide/domain/ui-model/element-modeles/property/3d';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import _merge from 'lodash/merge';

class ModelState {
    // 数据发生变化
    public dataChanged: boolean;

    public constructor() {
        this.dataChanged = false;
    }

    public reset() {
        this.dataChanged = false;
    }
}

class ElementBaseModel extends BaseModel implements IElementBaseModel {
    @observable public isPlaceholder = false; // 是否是占位符
    @observable public isCustomPlaceholder = false; // 如果是占位符，是否被修改过
    @observable public sourceType = ''; // SourceType 字符串类型
    @observable public previewChange = 0;
    @observable public animList: IObservableArray<AnimationModel> | any[] = [];
    @observable public hidden: 0 | 1 = 0; // 是否隐藏
    @observable public operationFrame: IFrame | null = null;

    public sdkData: ElementBaseSdkTypeV3 = null;
    public declare props: IElementModelProps;

    public relatedUIModelRefs: any[] = [];
    public state = new ModelState();
    public textStyleList?: any[];
    public placeHolder?: IPlaceHolderProps;
    protected styleHandler: StyleHandler = new StyleHandler();
    private regionStateMap = new Map();
    protected linkProperty?: ILinkProperty;
    protected frameInstance?: IFrameProperty;
    protected fillInstance?: IFillProperty;
    // 效果操作实例
    protected effectInstance?: IEffect;
    protected borderInstance?: IBorderProperty; // 边框操作实例
    protected property3dInstance?: I3DProperty; // 3d 相关操作实例
    protected lockInstance?: ILockProperty; // 锁定纵横比操作实例
    protected parser?: IBaseGraphics;
    protected fillFormat?: BaseFill;
    protected _scene3d?: IScene3D;
    protected _shape3d?: IShape3D;

    public constructor(props?: {}) {
        super(props);
        this.styleHandler.baseModel = this;
    }

    protected cloneJsonObject() {
        const clone = super.cloneJsonObject();
        delete clone.linkProperty;
        delete clone.frameInstance;
        delete clone.fillInstance;
        delete clone.effectInstance;
        delete clone.borderInstance;
        delete clone.property3dInstance;
        delete clone.lockInstance;
        delete clone._scene3d;
        delete clone._shape3d;
        return clone;
    }

    public isAttachment(): boolean {
        return false;
    }

    public isAttachmentUploaded(): boolean {
        return false;
    }

    public isGhostAttachment(region?: string): boolean {
        return !!this.getAttachmentManager()?.isGhostAttachment(this, region);
    }

    // eslint-disable-next-line @typescript-eslint/class-literal-property-style
    public get selectable() {
        // 不要写成 readOnly
        return true;
    }

    /**
     * 是否是插件
     */
    public get isAddon(): boolean {
        return !!this.props?.addon;
    }

    public get pathParser(): IBaseGraphics {
        // @ts-ignore
        const { width, height, bgShape, controlValues, bgLine, border, pathList } = this.props.style;
        if (isSketchStyle(border?.sketchStyle?.type) || !this.parser) {
            const type = border?.sketchStyle?.bgShape ?? (bgShape || bgLine) ?? GraphicsType.RECT;
            return createGraphicsParser({
                type,
                controlValues,
                width,
                height,
                pathList,
            });
        }
        return this.parser;
    }

    public set pathParser(parser: IBaseGraphics) {
        this.parser = parser;
    }

    public get shape3d(): IShape3D | undefined {
        return this._shape3d;
    }

    public set shape3d(shape3d: IShape3D | undefined) {
        this._shape3d = shape3d;
    }

    public get scene3d(): IScene3D | undefined {
        if (this._scene3d) {
            return this._scene3d;
        }
        return this.get3DProperty().getElementBaseScene3D();
    }

    public set scene3d(scene: IScene3D | undefined) {
        this._scene3d = scene;
    }


    public getStateForRegion(region: string): object {
        return this.regionStateMap.get(region);
    }

    public addStateForRegion(region: string): void {
        if (!this.regionStateMap.has(region)) {
            this.regionStateMap.set(region, {});
        }
    }

    public removeStateForRegion(region: string): void {
        if (this.regionStateMap.has(region)) {
            this.regionStateMap.delete(region);
        }
    }

    public saveState(state): void {
        this.regionStateMap.forEach((value) => {
            Object.assign(value, state);
        });
    }

    public cleanStateForRegion(region: string): void {
        this.regionStateMap.set(region, {});
    }

    public async bindSDK(sdkData): Promise<void> {
        if (this.parent) {
            this.asyncConvertChildren = this.parent.asyncConvertChildren;
        }
        const promise = super.bindSDK(sdkData);
        if (this.parent?.asyncConvertChildren) {
            await promise;
        }
    }

    public unbindSDK() {
        super.unbindSDK();
        if (this.selected) {
            if (this.selectionService) {
                this.selectionService.selectedElements.remove(this);
            }
        }
    }

    public resetFrame = (useProps = false) => {
        let sdk = this.sdkData;
        if (useProps) {
            sdk = this.prepareSDKPropsData(_cloneDeep(this.props));
        }
        ElementCommonPropertyConverter.convertFrameInfoFromSdkToUi(sdk?.props as IShapeData, this);
    };

    public removeRelatedUIModelRefs(uiModel: IElementBaseModel): void {
        if (this.relatedUIModelRefs?.length > 0) {
            for (let i = 0; i < this.relatedUIModelRefs.length; i++) {
                if (this.relatedUIModelRefs[i] === uiModel) {
                    this.relatedUIModelRefs.splice(i, 1);
                }
            }
        }
    }

    public get rootGroupModel(): IGroupModel | null {
        let rootGroup = null;
        enumerateGroup(this, (groupModel) => {
            rootGroup = groupModel;
        });
        return rootGroup;
    }

    public get rootSmartArtModel(): ISmartArtModel | null {
        let rootGroup = null;
        enumerateSimilarGroup(this, (groupModel) => {
            rootGroup = groupModel;
        });
        return rootGroup;
    }

    public getFrame(): IFrameProperty {
        if (!this.frameInstance) {
            this.frameInstance = new Frame(this);
        }
        return this.frameInstance;
    }

    public getLink(): ILinkProperty {
        if (!this.linkProperty) {
            this.linkProperty = new LinkProperty(this);
        }
        return this.linkProperty;
    }

    public static async convertDataSdkToUi(sdkData, uiData, rawSdk, optionObject?) {
        try {
            await super.convertDataSdkToUi(sdkData, uiData, rawSdk, optionObject);
        } catch (e) {
            if (_get(uiData, 'inTable') === true) {
                throw e;
            } else {
                const modelInfo = {
                    key: uiData.key,
                    type: uiData.type,
                    inGroup: uiData.parent && uiData.parent.type === UIModelType.GROUP,
                    slideIndex: uiData.presentationRef.indexOfSlide(uiData.slideRef),
                };
                const uiModel = uiData;
                uiModel.type = UIModelType.EMPTY;
                (AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices).viewableServiceLayer
                    .tipsManager.showConvertExceptionModal(modelInfo, e);
            }
        }
    }

    public static didConvertDataSdkToUi(sdkData, uiData, rawSdk, changesetInfo?) {
        super.didConvertDataSdkToUi(sdkData, uiData, rawSdk, changesetInfo);
    }

    public getBackgroundFill(): IFillProperty {
        if (!this.fillInstance) {
            this.fillInstance = new Fill(this);
        }
        return this.fillInstance;
    }

    /**
     * Effect相关接口
     */
    public getEffect(): IEffect {
        if (!this.effectInstance) {
            this.effectInstance = new Effect(this);
        }
        return this.effectInstance;
    }

    public getBorder(): IBorderProperty {
        if (!this.borderInstance) {
            this.borderInstance = new Border(this);
        }
        return this.borderInstance;
    }

    public get3DProperty(): I3DProperty {
        if (!this.property3dInstance) {
            this.property3dInstance = new ThreeDProperty(this);
        }
        return this.property3dInstance;
    }

    /**
     * 锁定纵横比相关接口
     */
    public getLock(): ILockProperty {
        if (!this.lockInstance) {
            this.lockInstance = new Lock(this);
        }
        return this.lockInstance;
    }

    public updateDescription(descr: Record<string, any>) {
        this.sdkData?.api.updateProps({
            nvPr: {
                descr: JSON.stringify({
                    ...descr,
                }),
            },
        });
    }

    public deleteDescription() {
        this.sdkData?.api.updateProps({
            nvPr: {
                descr: null,
            },
        });
    }

    public get modifiedProps(): any {
        const props: any = {};
        const styles: any = this.props.style || {};
        if (this.fillFormat) {
            styles.fillBackground = this.fillFormat.toPropValue();
        }
        if (Object.keys(styles).length > 0) {
            props.style = styles;
        }
        return props;
    }

    protected getUpdateAndRemoveProps(updateProps): IUpdateAndRemoveProps {
        return UpdateAndRemoveLogic.convertToUpdateAndRemove(updateProps, this.props);
    }

    public get isFromMaster(): boolean {
        return this.sourceType === SourceType.MASTER;
    }

    public get isFromLayout(): boolean {
        return this.sourceType === SourceType.LAYOUT;
    }

    public get isFromSlide(): boolean {
        return this.sourceType === SourceType.SLIDE;
    }

    public get titleTextStyleList() {
        return this.isFromMaster && this.masterRef?.titleTextStyleList;
    }

    public get bodyTextStyleList() {
        return this.isFromMaster && this.masterRef?.bodyTextStyleList;
    }

    public get otherTextStyleList() {
        return this.isFromMaster && this.masterRef?.otherTextStyleList;
    }

    public get placeHolderTypeNum() {
        const typeString: string = _get(this, 'placeHolder.type');
        return STPlaceholderType[typeString];
    }

    public get isTitleOrCtrTitlePl() {
        if (!this.isPlaceholder) return false;
        return [STPlaceholderType.title, STPlaceholderType.ctrTitle].includes(this.placeHolderTypeNum);
    }

    public get isCtrTitlePl() {
        if (!this.isPlaceholder) return false;
        return STPlaceholderType.ctrTitle === this.placeHolderTypeNum;
    }

    public get isSubTitlePl() {
        if (!this.isPlaceholder) return false;
        return [STPlaceholderType.subTitle].includes(this.placeHolderTypeNum);
    }

    public get isFooterPl() {
        if (!this.isPlaceholder) return false;
        return this.placeHolderTypeNum === STPlaceholderType.ftr;
    }

    public get isBodyPl() {
        if (!this.isPlaceholder) return false;
        return this.placeHolderTypeNum === STPlaceholderType.body;
    }

    public get isDatePl() {
        if (!this.isPlaceholder) return false;
        return this.placeHolderTypeNum === STPlaceholderType.dt;
    }

    public get isSlideNumPl() {
        if (!this.isPlaceholder) return false;
        return this.placeHolderTypeNum === STPlaceholderType.sldNum;
    }

    public get isPlFromMasterOrLayout() {
        return this.isPlaceholder && !this.isFromSlide;
    }

    public get showOriginalText() {
        return this.getMasterLayoutEditService()?.isMasterEditType;
    }

    public get zIndex(): number {
        const { parent } = this;
        if (!parent) {
            return 0;
        }
        const children = [...parent.children];
        const zindex = children.indexOf(this);
        return zindex;
    }

    /**
     * 清除border、fill、effect属性
     */
    public resetStyle() {
        const { style } = this.props;
        let resetStyle: Partial<IElementModelStyle> = {};
        if (style.border) {
            resetStyle = _merge(resetStyle, {
                border: null,
            });
        }
        if (style.fillBackground) {
            resetStyle = _merge(resetStyle, {
                fillBackground: null,
            });
        }
        if (style.effect) {
            resetStyle = _merge(resetStyle, {
                effect: null,
            });
        }
        this.removeProps({ style: resetStyle });
    }

    public updateShapeFastStyle(style: ICTShapeStyle | null) {
        this.sdkData?.api.updateProps({ style });
    }
}

export default ElementBaseModel;
